import type { AppLocale } from '@/i18n';
import { parseIsoDate } from '@/shared/lib/dates';
import { USER_ROLES, type UserRole } from '@/shared/model/enums';

import type { AnalyticsGranularity, SignupsTimeseriesPoint } from '../model/analytics';

/** A compact, locale-aware label for a repeating chart-axis tick. Deliberately
 * Gregorian-only (unlike the shared `formatDualDate`) — a dual Hijri/Gregorian
 * string on every one of up to ~90 axis ticks would be unreadable. The full
 * dual-date string is still used for the accompanying accessible data table
 * (see `LineChart`'s `fullLabels`), so the dual-calendar rule is honored for
 * every date value the UI actually exposes, just not repeated on the axis. */
export function formatBucketTick(
  iso: string,
  granularity: AnalyticsGranularity,
  locale: AppLocale,
): string {
  const date = parseIsoDate(iso);
  if (granularity === 'month') {
    return new Intl.DateTimeFormat(locale, { month: 'short', year: 'numeric' }).format(date);
  }
  return new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short' }).format(date);
}

/** Rounds to a fixed number of decimals and formats using the locale's digit
 * conventions (used for averages like avg-applicants / avg-time-to-fill). */
export function formatDecimal(value: number, locale: AppLocale, maximumFractionDigits = 1): string {
  return new Intl.NumberFormat(locale, { maximumFractionDigits }).format(value);
}

/** Plain integer count, locale-formatted (thousand separators etc.). */
export function formatCount(value: number, locale: AppLocale): string {
  return new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(value);
}

export interface PivotedSignups {
  /** Sorted ascending ISO bucket dates. */
  readonly buckets: readonly string[];
  readonly byRole: Readonly<Record<UserRole, readonly number[]>>;
  readonly freelance: readonly number[];
  readonly appointed: readonly number[];
}

/**
 * Pivots the flat `(bucket, role, is_freelance, count)` rows from
 * `analytics_signups_timeseries` into two aligned series sets — by role, and
 * by freelance/appointed mode (`is_freelance` true/false; per the D-014 flag
 * model, `false` here means every other captured signup, which in practice is
 * the appointed-only masjid_admin cohort).
 */
export function pivotSignups(points: readonly SignupsTimeseriesPoint[]): PivotedSignups {
  const buckets = Array.from(new Set(points.map((p) => p.bucket))).sort();
  const bucketIndex = new Map(buckets.map((b, i) => [b, i]));

  const byRole = Object.fromEntries(
    USER_ROLES.map((role) => [role, new Array<number>(buckets.length).fill(0)]),
  ) as Record<UserRole, number[]>;
  const freelance = new Array<number>(buckets.length).fill(0);
  const appointed = new Array<number>(buckets.length).fill(0);

  for (const p of points) {
    const i = bucketIndex.get(p.bucket);
    if (i === undefined) continue;
    const roleSeries = byRole[p.role];
    roleSeries[i] = (roleSeries[i] ?? 0) + p.count;
    if (p.isFreelance) freelance[i] = (freelance[i] ?? 0) + p.count;
    else appointed[i] = (appointed[i] ?? 0) + p.count;
  }

  return { buckets, byRole, freelance, appointed };
}
